import { events } from "@constants/tracking";
import type { RedeemablePurchase } from "@memorang/ui/components/AutoRedeem/types";
import { trackCustomEvent } from "analytics";
import {
	applyAccessCode,
	getActiveCodes,
} from "api/commerce/access-code-service";
import type { ActiveCodesResponse } from "api/commerce/types";

type AutoRedeemActiveCodeParams = {
	sku?: string;
	source?: string;
};

const AUTO_REDEEM_PROMPT_INTERVAL_MS = 2 * 24 * 60 * 60 * 1000;

export const shouldPromptAutoRedeem = (lastPromptAt?: number) => {
	if (!lastPromptAt) {
		return true;
	}
	return Date.now() - lastPromptAt >= AUTO_REDEEM_PROMPT_INTERVAL_MS;
};

const mapActiveCodesToPurchases = (
	activeCodes: ActiveCodesResponse[],
): RedeemablePurchase[] =>
	activeCodes
		.filter((activeCode) => activeCode.codes.length > 0)
		.map((activeCode) => ({
			id: activeCode.bundle.id,
			title: activeCode.bundle.name,
			icon: activeCode.bundle.icon.url,
			description: activeCode.bundle.description,
			codes: activeCode.codes,
			bundleType: activeCode.bundle.bundleType,
		}));

export const fetchActivePurchases = async () =>
	mapActiveCodesToPurchases(await getActiveCodes({}));

export const getRedeemedExamId = (
	result: Awaited<ReturnType<typeof applyAccessCode>>,
) =>
	typeof result.data.examId === "string"
		? result.data.examId
		: result.productData?.[0]?.examId;

export const getRedeemedExamName = (
	result: Awaited<ReturnType<typeof applyAccessCode>>,
) =>
	typeof result.data.examName === "string"
		? result.data.examName
		: result.productData?.[0]?.examName;

export const autoRedeemActiveCode = async ({
	sku,
	source,
}: AutoRedeemActiveCodeParams) => {
	const activeCodesResult = await getActiveCodes({ sku });
	const accessCode = activeCodesResult[0]?.codes[0];

	if (!accessCode) {
		return null;
	}

	trackCustomEvent({
		eventName: events.promoCodeEntered,
		source,
		examId: accessCode,
	});

	const result = await applyAccessCode({ accessCode });

	if (!result.success) {
		return null;
	}

	const examId = getRedeemedExamId(result);

	trackCustomEvent({
		eventName: events.promoCodeRedeemed,
		source,
		examId,
		examNumericId: result.data.examNumericId,
		code: accessCode,
	});

	return examId ?? null;
};
